import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { UsersApi } from '@core/api/users/users.api';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { User } from 'app/core/interfaces';
import { debounceTime, skip, switchMap } from 'rxjs/operators';
import english from '../../../../main/maintenances/i18n/en.json';
import spanish from '../../../../main/maintenances/i18n/es.json';
import portuguese from '../../../../main/maintenances/i18n/pt.json';

const INPUT_FIELD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AssigneesComponent),
  multi: true
};

@Component({
  selector: 'assignees',
  templateUrl: './assignees.component.html',
  styleUrls: ['./assignees.component.scss'],
  providers: [INPUT_FIELD_VALUE_ACCESSOR]
})
export class AssigneesComponent implements ControlValueAccessor, OnInit {
  @Input() required = false;
  @Input() disabled = false;
  @Input() floatLabel = true;
  @Input() appearance = 'outline';

  assignees: User[] = [];
  inputValue: FormControl = new FormControl();
  selectedAssignee: User;

  loading = false;

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private usersApi: UsersApi
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
  }

  ngOnInit(): void {
    if (this.required) {
      this.inputValue.setValidators(Validators.required);
    }

    if (this.disabled) {
      this.inputValue.disable();
    }

    this.inputValue.valueChanges.pipe(
      skip(1),
      debounceTime(500),
      switchMap((searchTerm) => {
        this.loading = true;
        this.assignees = [];

        return this.usersApi.search(
          { name_contains: searchTerm, type_in: ['all', 'admin'], archived: false },
          { orderColumn: 'name', order: 'asc' }
        );
      })
    ).subscribe((response) => {
      this.loading = false;
      this.assignees = response.items;
    });
  }

  onChange: (_: any) => void = () => {};
  onTouched: (_: any) => void = () => {};

  writeValue(assignee: User): void {
    if (assignee && assignee.id) {
      this.usersApi.searchOne(assignee.id).subscribe((requester) => {
        this.selectedAssignee = requester;
        this.inputValue.setErrors(null);
      });

      return;
    }

    this.deselectAssignee();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setSelectedAssignee(): void {
    if (this.inputValue.value) {
      this.selectedAssignee = this.assignees.find((requester) => (
        requester.id === this.inputValue.value
      ));

      this.onChange(this.selectedAssignee);
      this.inputValue.setErrors(null);
    }
  }

  deselectAssignee(): void {
    this.onChange(null);
    this.inputValue.setValue(null);
    this.inputValue.setErrors(null);
    this.selectedAssignee = null;
  }
}
